import { PaperPlaneRight, Plus, X } from "@phosphor-icons/react";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import TextareaAutosize from "react-textarea-autosize";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import ProfileImage from "common/components/ui/ProfileImage";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useConversationMessagesUpdates from "../../../datahooks/useConversationMessagesUpdates";
import useFileUpload from "../../../datahooks/useFileUpload";
import useInsertText from "../../../hooks/useInsertText";
import useFileUploadStore from "../../../stores/useFileUploadStore";
import { generateAudioFilename } from "../../../utils";
import EditMessageActions from "./EditMessageActions";
import FileAttachmentsList from "./FileAttachmentsList";
import MessageOptions from "./MessageOptions";
import RecordButton from "./RecordButton";
import SettingsPopover from "./SettingsPopover";
import VoiceRecorder from "./VoiceRecorder";

import type React from "react";
import type { UseFormReturn } from "react-hook-form";
import type { Conversation, ConversationMessage } from "../../../types";

function loadCtrlToSend() {
  const savedCtrlToSend = localStorage.getItem("ctrlToSend");
  if (savedCtrlToSend) {
    return JSON.parse(savedCtrlToSend);
  }
  return true;
}

interface MessageFieldProps {
  conversation: Conversation;
  editMessage: ConversationMessage;
  cancelEdit: () => void;
  onSend: () => void;
  isSendingMessage: boolean;
  formReturn: UseFormReturn<{ message: string }>;
  hasMessageFromAnotherUser: boolean;
}

export default function MessageField({
  conversation,
  editMessage,
  cancelEdit,
  onSend,
  isSendingMessage,
  formReturn,
  hasMessageFromAnotherUser,
}: MessageFieldProps) {
  const { register, setValue, getValues, reset, handleSubmit, watch } =
    formReturn;

  const {
    conversation_urn: conversationUrn,
    participants,
    owner: accountId,
    last_message: lastMessage,
    connected: isConnected,
  } = conversation ?? {};

  const { accounts } = useSelectedWorkspaceContext();
  const account = accounts.find(({ id }) => id === accountId);

  const { updateMessage, isUpdatingMessage } = useConversationMessagesUpdates({
    conversationUrn,
  });

  const { uploadFile, uploadAudio, isAudioUploading } = useFileUpload({
    accountId,
  });

  const [isRecording, setIsRecording] = useState(false);
  const [ctrlToSend, setCtrlToSend] = useState(loadCtrlToSend());
  const isDesktop = useTwBreakpoint("lg");

  const messageFieldRef = useRef<HTMLTextAreaElement>(null);
  const insertText = useInsertText(messageFieldRef, (string) =>
    setValue("message", string),
  );

  const [isMessageActionsOpen, setIsMessageActionsOpen] = useState(false);

  // Use Zustand store for file upload state
  const conversationKey = `${conversationUrn}:${accountId}`;
  const {
    getUploadedFiles,
    getUploadingFiles,
    addUploadedFile,
    removeUploadedFile,
    addUploadingFile,
    removeUploadingFile,
  } = useFileUploadStore();

  const uploadedFiles = getUploadedFiles(conversationKey);
  const uploadingFiles = getUploadingFiles(conversationKey);

  const prevEditMessageRef = useRef<ConversationMessage | null>(null);
  const prevConversationKey = useRef<string>(`${conversationUrn}:${accountId}`);

  const [uploadedAudio, setUploadedAudio] = useState<Blob | null>(null);

  useEffect(() => {
    if (editMessage !== prevEditMessageRef.current) {
      reset({ message: editMessage?.body });
      prevEditMessageRef.current = editMessage;
    }
  }, [editMessage, reset]);

  // Clear voice recorder state on conversation change
  useEffect(() => {
    const currentKey = `${conversationUrn}:${accountId}`;
    if (prevConversationKey.current !== currentKey) {
      setIsRecording(false);
      prevConversationKey.current = currentKey;
    }
  }, [conversationUrn, accountId]);

  async function handleFileUpload(file: File) {
    try {
      addUploadingFile(conversationKey, file);

      const response = await uploadFile({ file, filename: file.name });
      const fileUrl = URL.createObjectURL(file);

      const uploadedFileResponse = {
        id: response.file_identifier,
        name: response.file_input.originalname,
        file_input: response.file_input,
        file_url: fileUrl,
      };

      addUploadedFile(conversationKey, uploadedFileResponse);
      removeUploadingFile(conversationKey, file);
    } catch (_err) {
      removeUploadingFile(conversationKey, file);
    }
  }

  function removeFile(fileId: string) {
    removeUploadedFile(conversationKey, fileId);
  }

  function removeUploadingFileHandler(file: File) {
    removeUploadingFile(conversationKey, file);
  }

  async function onSave({ message }: { message: string }): Promise<void> {
    if (message.trim() !== editMessage.body) {
      await updateMessage({
        accountId,
        message,
        messageUrn: editMessage.urn,
      });
    }
    cancelEdit();
  }

  function onConfirm(): void {
    if (editMessage) {
      handleSubmit(onSave)();
      return;
    }
    onSend();
  }

  function onKeyDown(e: React.KeyboardEvent): void {
    if (e.key === "Enter") {
      if (e.ctrlKey || e.metaKey) {
        if (ctrlToSend) {
          onConfirm();
        } else {
          setValue("message", `${getValues("message")}\n`);
        }
      } else if (!ctrlToSend) {
        onConfirm();
        e.preventDefault();
      }
    }
  }

  const isEmptyMessage =
    !watch("message")?.trim() && !uploadedFiles.length && !uploadedAudio;
  const { is_lead: isLead } = participants[0] || {};

  // Disable messaging if recipient isn’t a lead, not connected, or last message was declined
  const isMessagingDisabled =
    (!isLead && !isConnected) || lastMessage?.declined === true;

  // Messaging is blocked if disabled with no incoming message, or user is not logged in
  const isMessagingBlocked =
    (isMessagingDisabled && !hasMessageFromAnotherUser) ||
    account?.state !== "LoggedIn";

  const { ref, ...registerRest } = register("message");

  const sender = accounts.find(({ id }) => id === conversation.owner);

  const messageOptions = (
    <MessageOptions
      isMessagingDisabled={isMessagingBlocked}
      uploadedFilesCount={uploadedFiles.length}
      participants={participants}
      ctrlToSend={ctrlToSend}
      setCtrlToSend={setCtrlToSend}
      onFileUpload={handleFileUpload}
      insertText={insertText}
      isRecording={isRecording}
      setIsRecording={setIsRecording}
    />
  );

  // Handle audio upload
  function handleAudioUpload(audioBlob: Blob) {
    const filename = generateAudioFilename(audioBlob.type);

    uploadAudio({
      file: audioBlob,
      filename,
      recipients: participants.map((participant) => participant.id),
    }).then(() => {
      setIsRecording(false);
      setUploadedAudio(null);
    });
  }

  // Handle send button click
  function handleSend() {
    if (uploadedAudio) {
      handleAudioUpload(uploadedAudio);
    }

    onSend();
  }

  const sendButton = (
    <Button
      variant="default-purple"
      intent={isDesktop ? "default" : "iconOnly"}
      isLoading={isSendingMessage || isAudioUploading}
      disabled={isEmptyMessage || isMessagingBlocked}
      leftIcon={isDesktop && <PaperPlaneRight />}
      onClick={handleSend}
      size={isDesktop ? "sm" : "md"}
    >
      {isDesktop ? "Send" : <PaperPlaneRight />}
    </Button>
  );

  return (
    <>
      <div
        className={clsx(
          isMessagingBlocked ? "text-black-500" : "text-black-950",
          "flex w-full items-center gap-2 rounded-t-3xl border border-b-0 border-white text-sm transition-all lg:flex-col lg:items-stretch lg:gap-0 lg:rounded-3xl lg:border-b lg:border-black-200 lg:bg-white lg:mx-2 lg:mb-2 lg:w-[calc(100%_-_16px)]",
          "has-[textarea:hover:focus-within]:bg-white",
          "p-2 has-[textarea:focus-visible]:border-purple-400",
          !isDesktop && isRecording && "justify-between border-purple-400",
        )}
      >
        <RenderIf condition={!isDesktop && !isRecording}>
          <Button
            variant={isMessageActionsOpen ? "secondary-black" : "ghost-black"}
            intent="iconOnly"
            onClick={() => setIsMessageActionsOpen(!isMessageActionsOpen)}
          >
            {isMessageActionsOpen ? <X /> : <Plus />}
          </Button>
        </RenderIf>

        {!isDesktop && isRecording ? (
          <VoiceRecorder
            onAudioReady={(audioBlob) => {
              setUploadedAudio(audioBlob);
            }}
            onRecordingDelete={() => setIsRecording(false)}
            autoStart
          />
        ) : (
          <div className="flex w-full flex-col gap-2 overflow-x-hidden rounded-5xl bg-black-100 p-2 lg:gap-0 lg:rounded-none lg:bg-transparent lg:p-0">
            <div className="flex flex-row gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <ProfileImage
                    size="xs"
                    className={clsx([
                      isMessagingBlocked && "opacity-50",
                      "mt-0.5 rounded-full",
                    ])}
                    src={sender?.picture_url}
                    alt={sender?.full_name}
                  />
                </TooltipTrigger>

                <TooltipContent className="w-fit">
                  {sender?.full_name}
                </TooltipContent>
              </Tooltip>

              {isRecording && isDesktop ? (
                <VoiceRecorder
                  onAudioReady={(audioBlob) => {
                    setUploadedAudio(audioBlob);
                  }}
                  onRecordingDelete={() => setIsRecording(false)}
                  autoStart
                />
              ) : (
                <TextareaAutosize
                  minRows={isDesktop ? 2 : 1}
                  maxRows={11.3}
                  disabled={isMessagingBlocked}
                  placeholder="Write your message here"
                  onKeyDown={onKeyDown}
                  className="flex-1 resize-none bg-transparent py-1 outline-none scrollbar-thin"
                  ref={(el) => {
                    ref(el);
                    messageFieldRef.current = el;
                  }}
                  {...registerRest}
                />
              )}
            </div>

            <RenderIf
              condition={uploadedFiles.length > 0 || uploadingFiles.length > 0}
            >
              <FileAttachmentsList
                uploadingFiles={uploadingFiles}
                uploadedFiles={uploadedFiles}
                removeUploadingFileHandler={removeUploadingFileHandler}
                removeFile={removeFile}
              />
            </RenderIf>
          </div>
        )}

        {(isEmptyMessage || isMessagingBlocked) &&
        !isDesktop &&
        !isRecording ? (
          <RecordButton
            isRecording={isRecording}
            setIsRecording={setIsRecording}
            isDisabled={isMessagingDisabled}
          />
        ) : (
          <RenderIf condition={!isDesktop}>{sendButton}</RenderIf>
        )}

        {/* Note: Add padding right on the desktop because of the Intercom widget overlap */}
        <div className="mt-2 hidden flex-row items-center justify-between border-t border-black-200 pt-2 lg:flex">
          {messageOptions}

          {editMessage ? (
            <EditMessageActions
              isUpdatingMessage={isUpdatingMessage}
              onCancel={cancelEdit}
              onReset={() => reset({ message: "" })}
              onConfirm={onConfirm}
            />
          ) : (
            <div className="flex flex-row items-center justify-between gap-2">
              {/* Settings Popover */}
              <SettingsPopover
                ctrlToSend={ctrlToSend}
                setCtrlToSend={setCtrlToSend}
                isDisabled={isMessagingBlocked}
              />

              <RenderIf condition={isDesktop}>{sendButton}</RenderIf>
            </div>
          )}
        </div>
      </div>

      <RenderIf condition={!isDesktop && isMessageActionsOpen && !isRecording}>
        <div className="border-t border-black-200 p-2">{messageOptions}</div>
      </RenderIf>
    </>
  );
}
